import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

const ReservationsList = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/reservations`);
      setReservations(response.data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des réservations');
      setLoading(false);
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await axios.patch(`${API}/reservations/${reservationId}/status`, {
        status: newStatus
      });
      fetchReservations(); // Refresh the list
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'confirmed':
        return 'Confirmée';
      case 'cancelled':
        return 'Annulée';
      case 'completed':
        return 'Terminée';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-8">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Réservations VTC</h2>
        <Button onClick={fetchReservations} variant="outline">
          Actualiser
        </Button>
      </div>

      {reservations.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">Aucune réservation trouvée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reservations.map((reservation) => (
            <Card key={reservation.id} className="border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {reservation.service}
                  </CardTitle>
                  <Badge className={getStatusColor(reservation.status)}>
                    {getStatusText(reservation.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Informations client</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>Nom:</strong> {reservation.name}</p>
                      <p><strong>Téléphone:</strong> {reservation.phone}</p>
                      <p><strong>Email:</strong> {reservation.email}</p>
                      <p><strong>Passagers:</strong> {reservation.passengers}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Détails du trajet</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>Date:</strong> {reservation.pickup_date}</p>
                      <p><strong>Heure:</strong> {reservation.pickup_time}</p>
                      <p><strong>Départ:</strong> {reservation.pickup_address}</p>
                      <p><strong>Destination:</strong> {reservation.destination}</p>
                    </div>
                  </div>
                </div>
                
                {reservation.notes && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm text-gray-600">{reservation.notes}</p>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  {reservation.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateReservationStatus(reservation.id, 'confirmed')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Confirmer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateReservationStatus(reservation.id, 'cancelled')}
                      >
                        Annuler
                      </Button>
                    </>
                  )}
                  {reservation.status === 'confirmed' && (
                    <Button
                      size="sm"
                      onClick={() => updateReservationStatus(reservation.id, 'completed')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Marquer comme terminée
                    </Button>
                  )}
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  <p>Créée le: {new Date(reservation.created_at).toLocaleString('fr-FR')}</p>
                  <p>ID: {reservation.id}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReservationsList;